import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { Swiper as SwiperType } from 'swiper'
import { Navigation, Pagination, Thumbs } from 'swiper/modules'
import { SwiperRef } from 'swiper/react'

import {
  MediaAsContents,
  TypeStyle,
  fromMd,
  untilMd,
  useIsBrowser,
  useSupportsHover,
  zIndexForeground,
} from '@syconium/little-miss-figgy'

import { GalleryImageStateContainer } from '../../../app/_components/pages/product-pages/shared/providers/GalleryImageStateContainer'
import { PaginationPageIndicator } from '../../../app/_components/pagination/PaginationPageIndicator.client'
import {
  SwiperSlideWithAnalytics,
  SwiperWithAnalytics,
} from '../../../app/_components/swiper/SwiperWithAnalytics'
import { useFavorites } from '../../../app/_providers/FavoritesProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'

import { IProps } from './IProps'
import { NavigationArrows } from './NavigationArrows'
import * as Styled from './styles'
import { ThumbNailsDisplay } from './ThumbNailsDisplay'

const swiperModules = [Navigation, Pagination, Thumbs]

const StyledPaginationPageIndicator = styled(PaginationPageIndicator)<{ bottomOffset?: number }>`
  position: absolute;
  left: ${o => o.theme.spacing(4)};
  bottom: ${o => (o.bottomOffset ? o.bottomOffset : 18)}px;
  z-index: ${zIndexForeground};
  ${untilMd} {
    opacity: 0.8;
  }
`

export const SwiperImageSlider: React.FC<IProps> = ({
  fitModelCaption,
  iconBadge,
  shouldDisplayIconBadge,
  showImageZoom,
  galleryImageCaptions,
  ...rest
}) => {
  const {
    magnolia: {
      general: { playVideo, pauseVideo, muteVideo, unmuteVideo },
    },
    pages: {
      product: {
        gallery: { catwalkVideoAriaLabel, ugcVideoAriaLabel },
      },
    },
  } = useTranslation()

  const { allAssets, currentAssetIndex, setCurrentAssetIndex, isImageZoomShown, imageDescription } =
    GalleryImageStateContainer.useContainer()

  const isProductCatalogImageOrKitColoredImage = useCallback(
    (index: number) => {
      const typename = allAssets[index]?.__typename
      return typename === 'ProductCatalogImage' || typename === 'KitColoredImage'
    },
    [allAssets]
  )

  // Search for ugc, use catwalk if no ugc found
  const videoAssetIndex = useMemo(() => {
    if (allAssets.length === 0) {
      return -1
    }

    let catwalkIndex = -1

    for (const [index, asset] of allAssets.entries()) {
      if (asset.__typename === 'ProductCatalogVideo') {
        if (asset.type === 'UGC') {
          return index
        }
        if (catwalkIndex === -1 && asset.type === 'CATWALK') {
          catwalkIndex = index
        }
      }
    }

    return catwalkIndex
  }, [allAssets])

  const shouldShowGalleryPlayButton = videoAssetIndex > -1 && typeof showImageZoom === 'function'

  const swiperRef = useRef<SwiperType>(null)
  const swiperRefInit = useRef<SwiperRef>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>()

  const isBrowser = useIsBrowser()

  // It is OK to use JavaScript device checking here
  // since the feature we will be toggling (pagination)
  // will not cause any CLS if it changes between the server
  // and the client. Don't use this variable for other
  // visual changes in our rendered output.
  const hoverSupported = useSupportsHover()

  useUpdateEffect(() => {
    if (!isImageZoomShown) {
      if (swiperRefInit.current?.swiper) {
        const { swiper } = swiperRefInit.current
        swiper.slideToLoop(currentAssetIndex, 0, false)
      }
    }
  }, [isImageZoomShown])

  const onSwiperSlideChangeCallback = useCallback(
    (swiper: SwiperType) => {
      setCurrentAssetIndex(swiper.realIndex)
    },
    [setCurrentAssetIndex]
  )
  const onBeforeSwiperInit = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper
  }, [])
  const currentGalleryImageCaption = galleryImageCaptions?.find(
    caption => caption.index === currentAssetIndex
  )?.caption

  return (
    <Styled.RootWrapper $constrainHeight>
      <Styled.HideIfShortOnSpace>
        <ThumbNailsDisplay onThumbsSwiperInit={setThumbsSwiper} gallerySwiperRef={swiperRef} />
      </Styled.HideIfShortOnSpace>
      <Styled.GallerySliderWrapper $constrainHeight>
        <SwiperWithAnalytics
          loop={isBrowser === true && allAssets.length > 1}
          modules={swiperModules}
          onBeforeInit={onBeforeSwiperInit}
          ref={swiperRefInit}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          thumbs={{ swiper: thumbsSwiper }}
          zoom={true}
          onSlideChange={onSwiperSlideChangeCallback}
          {...rest}
        >
          <MediaAsContents lessThan='md'>
            <StyledPaginationPageIndicator
              currentIndex={currentAssetIndex + 1}
              totalCount={allAssets.length}
              bottomOffset={fitModelCaption ? 16 : 0}
            />
          </MediaAsContents>

          {allAssets.map((slide, index) => {
            // Only render one image for serverside render since this 3rd
            // party swiper does not support SSR... cry a little
            if (!isBrowser && index !== currentAssetIndex) return null

            if (
              slide.__typename === 'ProductCatalogImage' ||
              slide.__typename === 'KitColoredImage'
            ) {
              return (
                <SwiperSlideWithAnalytics
                  key={slide.url}
                  analyticsLabel={`Slide ${index + 1}`}
                  analyticsVariant='image'
                  onClick={() => showImageZoom && showImageZoom(swiperRef.current?.realIndex)}
                >
                  <Styled.GalleryImageWrapper>
                    <Styled.GalleryImage
                      src={slide.url}
                      alt={slide.altText || imageDescription || ''}
                      fetchPriority={index === 0 ? 'high' : undefined}
                      loading='default'
                      widths={{ unit: 'vw', sm: 100, md: 66 }}
                      aspectRatios={{
                        sm: 0.8,
                        md: 0.8,
                      }}
                    />
                    {shouldShowGalleryPlayButton && index === 0 && (
                      <Styled.GalleryImagePlayButton
                        paused
                        aria-label={playVideo}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          // Prevents triggering the image zoom
                          e.stopPropagation()
                          // Opens video modal
                          showImageZoom(videoAssetIndex)
                        }}
                      />
                    )}
                  </Styled.GalleryImageWrapper>
                </SwiperSlideWithAnalytics>
              )
            } else if (slide.__typename === 'ProductCatalogVideo') {
              if (slide.type === 'CATWALK') {
                return (
                  <SwiperSlideWithAnalytics
                    key={slide.url}
                    analyticsLabel={`Slide ${index + 1}`}
                    analyticsVariant='catwalk-video'
                  >
                    <Styled.GalleryVideoWrapper $constrainHeight>
                      <Styled.GalleryVideo
                        ariaLabel={catwalkVideoAriaLabel}
                        autoPlay
                        muted
                        loop
                        loading='defer'
                        playsInline
                        src={slide.url}
                        type={'video/mp4'}
                        displayPauseButton
                        aspectRatios={{
                          sm: 0.8,
                          md: 0.8,
                        }}
                        screenReaderStrings={{
                          pauseText: pauseVideo,
                          playText: playVideo,
                          muteText: muteVideo,
                          unMuteText: unmuteVideo,
                        }}
                        onClick={event => {
                          if (event.target === event.currentTarget) {
                            showImageZoom && showImageZoom(swiperRef.current?.realIndex)
                          }
                        }}
                      />
                    </Styled.GalleryVideoWrapper>
                  </SwiperSlideWithAnalytics>
                )
              }

              if (slide.type === 'UGC') {
                return (
                  <SwiperSlideWithAnalytics
                    key={slide.url}
                    analyticsLabel={`Slide ${index + 1}`}
                    analyticsVariant='ugc-video'
                  >
                    <Styled.GalleryVideoWrapper $constrainHeight>
                      <Styled.PDPAudioVideo
                        ariaLabel={ugcVideoAriaLabel}
                        autoPlay
                        loop
                        muted
                        playsInline
                        src={slide.url}
                        type={'video/mp4'}
                        displayPauseButton={true}
                        displayMuteButton={true}
                        aspectRatios={{
                          sm: 0.8,
                          md: 0.8,
                        }}
                        screenReaderStrings={{
                          pauseText: pauseVideo,
                          playText: playVideo,
                          muteText: muteVideo,
                          unMuteText: unmuteVideo,
                        }}
                        onClick={event => {
                          if (event.target === event.currentTarget) {
                            showImageZoom && showImageZoom(swiperRef.current?.realIndex)
                          }
                        }}
                        loading='defer'
                      />
                    </Styled.GalleryVideoWrapper>
                  </SwiperSlideWithAnalytics>
                )
              }

              return <SwiperSlideWithAnalytics key={slide.url} />
            } else {
              return (
                <SwiperSlideWithAnalytics
                  key={slide.key}
                  onClick={() => showImageZoom && showImageZoom(swiperRef.current?.realIndex)}
                >
                  {slide.children}
                </SwiperSlideWithAnalytics>
              )
            }
          })}
          {hoverSupported === true ? <NavigationArrows gallerySwiperRef={swiperRef} /> : null}
        </SwiperWithAnalytics>
        {fitModelCaption && currentAssetIndex === 0 ? fitModelCaption : null}

        {isProductCatalogImageOrKitColoredImage(currentAssetIndex) && currentGalleryImageCaption ? (
          <GalleryImageCaption>{currentGalleryImageCaption}</GalleryImageCaption>
        ) : null}
      </Styled.GallerySliderWrapper>
      {iconBadge && (
        <>
          <Styled.IconBadgeLarge
            {...iconBadge}
            iconBadgePlacement={'top left'}
            shouldDisplay={shouldDisplayIconBadge}
            size='large'
          />
          <Styled.IconBadgeExtraLarge
            {...iconBadge}
            iconBadgePlacement={'top left'}
            shouldDisplay={shouldDisplayIconBadge}
            size='extraLarge'
          />
        </>
      )}
    </Styled.RootWrapper>
  )
}

export const SliderFavoritesButtonContainer = styled(
  (props: { children?: React.ReactNode; className?: string }) => {
    const { status } = useFavorites()
    if (status === 'rejected') return null
    return <div className={props.className}>{props.children}</div>
  }
)`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  position: absolute;
  z-index: ${o => o.theme.zIndex.foregroundContent};
  top: ${o => o.theme.spacing(4)};
  right: ${o => o.theme.spacing(4)};
  background: ${o => o.theme.color.fill.background};

  > * {
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 0;
  }
`

const GalleryImageCaption = styled.div`
  position: absolute;
  padding: ${o => o.theme.spacing(2)};
  bottom: ${o => o.theme.spacing(4)};
  right: ${o => o.theme.spacing(4)};
  background: ${o => o.theme.color.fill.background};
  color: ${o => o.theme.color.text.primary};
  z-index: ${zIndexForeground};
  ${TypeStyle.css.bodyNano};
  border-radius: ${o => o.theme.borderRadius.sm};
  opacity: 0.85;
  font-weight: 600;
  ${fromMd} {
    padding: ${o => o.theme.spacing(3)};
    ${TypeStyle.css.bodyMedium};
    font-weight: 400;
    bottom: ${o => o.theme.spacing(5)};
    right: ${o => o.theme.spacing(5)};
    opacity: 1;
  }
`
